import React, { useEffect, forwardRef, useImperativeHandle } from 'react';
import './SidebarMenu.css';
import mainLogo from '../../assets/main-logo.webp';
import hoverLogo from '../../assets/hover-logo.webp';
import { useState } from 'react';

import { Badge } from '@mui/material';
import { useAuthenticatedSession } from '@/authenticatedSession/AuthenticatedSessionContext';

import { useMediaQuery } from '@mui/material';
import {
  Sun,
  Moon,
  UsersIcon,
  AlertCircle,
  CircleCheck,
  CircleIcon,
  ListTodoIcon,
} from 'lucide-react';
import {
  Box,
  Divider,
  Group,
  Paper,
  Stack,
  Switch,
  Text,
  Title,
  useMantineColorScheme,
} from '@mantine/core';
import { NavLink, Link, useLocation, useNavigate } from 'react-router-dom';
import UserMenu from './UserMenu';
import { useEmailAuthStatus } from '@/hooks/useEmailAuthStatus';
import gmailIcon from '../../assets/gmail-icon.webp';
import outlookIcon from '../../assets/outlook-icon.webp';

export interface SidebarMenuItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  badgeCount?: number;
  badgeColor?: string;
  isHidden?: boolean;
  failedCount?: number;
  draftCount?: number;
}

export interface SidebarMenuProps {
  menuItems: SidebarMenuItem[];
  collapsed: boolean;
  onOpenMobileMenu?: () => void;
  // closeMobileMenu?: () => void; // Optional, for mobile menu close functionality
  // isMobileMenuOpen?: boolean;   // Optional, for mobile menu visibility state
}

const SidebarMenu = forwardRef<
  { openMobileMenu: () => void },
  SidebarMenuProps
>(({ menuItems, collapsed, onOpenMobileMenu }, ref) => {
  const authSession = useAuthenticatedSession();
  const { authStatus: emailAuthStatus } = useEmailAuthStatus();
  const [logoSrc, setLogoSrc] = useState(mainLogo);
  const navigate = useNavigate();
  const location = useLocation();
  const [lastLocation, setLastLocation] = useState<string>();
  const isMobile = useMediaQuery('(max-width:768px)');
  const openMobileMenu = () => {
    // This is now handled by SiteHeader's Drawer
    onOpenMobileMenu?.();
  };

  useImperativeHandle(ref, () => ({
    openMobileMenu,
  }));
  const { colorScheme, toggleColorScheme } = useMantineColorScheme();

  useEffect(() => {
    if (!authSession) {
      return;
    }
    if (
      lastLocation?.startsWith('/tasks') &&
      !location.pathname.startsWith('/tasks')
    ) {
      authSession.db.updateTasks(
        { viewedByAssignedTo: authSession.userIds.firestore },
        [['assignedTo', '==', authSession.userIds.firestore]],
      );
    }
    // Clear Financials failed badge when navigating away from Financials page is not needed;
    // it's cleared when viewing Transactions tab (handled in TransactionsTab)
    setLastLocation(location.pathname);
  }, [authSession, location]);

  if (isMobile) {
    // On mobile, the menu is handled by SiteHeader's Drawer
    return null;
  }

  return (
    <div
      className={`sidebar-menu ${collapsed ? 'collapsed' : ''}`}
      style={{ backgroundColor: 'transparent' }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          rowGap: '16px',
          backgroundColor: 'transparent',
        }}
      >
        <div className='logo-container'>
          <Link to='/matters'>
            <img
              src={logoSrc}
              alt='Logo'
              className='logo'
              onMouseEnter={() => setLogoSrc(hoverLogo)}
              onMouseLeave={() => setLogoSrc(mainLogo)}
            />
          </Link>
        </div>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            rowGap: '8px',
            backgroundColor: 'transparent',
          }}
        >
          {menuItems.map((item, index) => {
            return item.isHidden ? null : (
              <MenuLink
                key={index.toString()}
                innerKey={index.toString()}
                label={item.label}
                path={item.path}
                icon={item.icon}
                badgeCount={item.badgeCount}
                badgeColor={item.badgeColor}
                collapsed={collapsed}
              />
            );
          })}
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '2rem',
          marginTop: 'auto',
          paddingBottom: '16px',
          alignItems: 'flex-start',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
            alignItems: 'center',
            width: '122px', // Magic number to approximately center the bottom menu items with the logo
          }}
        >
          <Switch
            size='md'
            color='dark.4'
            onLabel={<Sun size={16} />}
            offLabel={<Moon size={16} />}
            checked={colorScheme === 'dark'}
            onChange={() => toggleColorScheme()}
          />

          {authSession && <UserMenu authSession={authSession} />}
        </div>
        {/* <TodoList /> */}
      </div>
    </div>
  );
});

export default SidebarMenu;

const MenuLink = ({
  innerKey,
  label,
  path,
  icon,
  badgeCount,
  badgeColor,
  collapsed,
}: {
  innerKey: string;
  label: string;
  path: string;
  icon: React.ReactNode;
  collapsed: boolean;
  badgeCount: number | undefined;
  badgeColor?: string;
}) => {
  const location = useLocation();
  return (
    <NavLink
      key={innerKey}
      to={path}
      className={({ isActive }) =>
        `sidebar-menu-item menu-item-${innerKey} ${
          isActive ||
          (path === '/matters' && location.pathname.startsWith('/matter'))
            ? 'sidebar-menu-item-active'
            : ''
        }`
      }
    >
      <Badge
        badgeContent={badgeCount}
        sx={{
          '& .MuiBadge-badge': {
            backgroundColor: badgeColor ?? '#5970f6',
            border: '1px solid white',
            color: '#fff',
            top: '2px',
            right: collapsed ? '0' : '-6px',
          },
        }}
      >
        <div
          className='sidebar-menu-item-content'
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            rowGap: '0.3em',
          }}
        >
          {collapsed ? icon : null}
          <span className='sidebar-menu-item-text'>{label}</span>
        </div>
      </Badge>
    </NavLink>
  );
};

const TodoList = () => {
  const { authStatus: emailAuthStatus } = useEmailAuthStatus();
  if (emailAuthStatus.status !== 'unauthenticated') {
    return null;
  }
  if (!emailAuthStatus.provider) {
    return null;
  }
  return (
    <Box
      p='xs'
      w='calc(100% - 8px)'
      className='sidebar-menu-todo-list'
      mx='4px'
    >
      <Stack gap='xs'>
        <Group gap='xs'>
          <ListTodoIcon size={16} color='var(--mantine-color-pink-5)' />
          <Title order={5} size='sm' c='black' fw={600}>
            Finish setup
          </Title>
        </Group>

        <Box p='0' ml='md' className='sidebar-menu-todo-list-item'>
          <NavLink
            to='/email-account'
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: '8px',
              textDecoration: 'none',
              justifyContent: 'flex-start',
            }}
          >
            <Group gap='xs' wrap='nowrap' align='center'>
              <CircleIcon size={16} />
              <Text size='sm' mt='2px' fw={500} style={{ flex: '1 1 auto' }}>
                Connect{' '}
                {emailAuthStatus.provider === 'google' ? 'Email' : 'Email'}{' '}
              </Text>
            </Group>
          </NavLink>
        </Box>
      </Stack>
    </Box>
  );
};
